/**
 * cabinCurriculum.js — Admin müfredat (modül / ünite / ders) CRUD hooks.
 *
 * Read (public, auth'lu):
 *   GET /cabin/modules?lang           → { items: [...] }
 *   GET /cabin/modules/{slug}?lang    → { units: [{ lessons: [...] }] }
 *
 * Write (admin):
 *   POST   /admin/cabin/modules        PUT /admin/cabin/modules/{id}   DELETE …
 *   POST   /admin/cabin/units          PUT /admin/cabin/units/{id}     DELETE …
 *   POST   /admin/cabin/lessons        PUT /admin/cabin/lessons/{id}   DELETE …
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

// ─── Query keys ────────────────────────────────────────────────────────────────
export const curriculumKeys = {
  all: ['adminCabinCurriculum'],
  modules: () => [...curriculumKeys.all, 'modules'],
  moduleDetail: (slug) => [...curriculumKeys.all, 'moduleDetail', slug],
};

// ─── Reads ───────────────────────────────────────────────────────────────────

export function useAdminCabinModules(lang = 'tr') {
  return useQuery({
    queryKey: curriculumKeys.modules(),
    queryFn: () =>
      apiClient.get(`/cabin/modules?lang=${lang}`).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d.items ?? d.modules ?? [];
      }),
    staleTime: 30_000,
  });
}

export function useAdminCabinModuleDetail(slug, lang = 'tr') {
  return useQuery({
    queryKey: curriculumKeys.moduleDetail(slug),
    queryFn: () =>
      apiClient.get(`/cabin/modules/${slug}?lang=${lang}`).then((r) => r.data),
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

// Raw lesson (phases unresolved {tr,en} + content) — for the content editor.
export function useAdminLessonContent(lessonId) {
  return useQuery({
    queryKey: [...curriculumKeys.all, 'lesson', lessonId],
    queryFn: () => apiClient.get(`/admin/cabin/lessons/${lessonId}`).then((r) => r.data),
    enabled: Boolean(lessonId),
    staleTime: 10_000,
  });
}

// One-shot fetch for the lesson edit modal — returns raw {tr,en} title without resolving.
export async function fetchAdminLessonRaw(lessonId) {
  const res = await apiClient.get(`/admin/cabin/lessons/${lessonId}`);
  return res.data;
}

// ─── Module mutations ──────────────────────────────────────────────────────────

export function useCreateModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/cabin/modules', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.modules() }),
  });
}

export function useUpdateModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/cabin/modules/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.all }),
  });
}

export function useDeleteModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/cabin/modules/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.modules() }),
  });
}

// ─── Unit mutations ─────────────────────────────────────────────────────────────

export function useCreateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/cabin/units', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.all }),
  });
}

export function useUpdateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/cabin/units/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.all }),
  });
}

export function useDeleteUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/cabin/units/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.all }),
  });
}

// ─── Lesson mutations ────────────────────────────────────────────────────────────

export function useCreateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/cabin/lessons', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.all }),
  });
}

export function useUpdateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/cabin/lessons/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.all }),
  });
}

export function useDeleteLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/cabin/lessons/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: curriculumKeys.all }),
  });
}
